package org.teddapp.project.ui.notes

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.tedd.db.Note
import com.tedd.db.Note_revision
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import org.teddapp.project.db.nowMillis
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddDates
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.teddOverlayShadow

/** « 14 oct. à 09:41 » : le jour et l'heure (UTC, comme le reste de l'appli). */
internal fun dayAndTime(ms: Long): String {
    val day = TeddDates.formatShort(TeddDates.isoFromDays((ms / 86_400_000L).toInt())) ?: ""
    val minutes = ((ms / 60_000L) % 1440 + 1440) % 1440
    val h = (minutes / 60).toString().padStart(2, '0')
    val m = (minutes % 60).toString().padStart(2, '0')
    return "$day à $h:$m"
}

/**
 * Les versions passées d'une note, gardées sur cet appareil. On en choisit une, on la
 * lit, on la restaure. La version actuelle est mise de côté avant : restaurer ne perd
 * rien, et se défait en restaurant celle-là.
 */

/** La liste des versions d'une note : 210 dp par défaut, de 160 à 360. */
private val VERSIONS_PANE = org.teddapp.project.ui.PaneSpec("notes.versions", default = 210.dp, min = 160.dp, max = 360.dp, collapseBelow = 120.dp)

@Composable
internal fun NoteHistoryDialog(db: TeddDatabase, note: Note, draft: NoteDraft, onClose: () -> Unit) {
    var revisions by remember(note.id) { mutableStateOf<List<Note_revision>>(emptyList()) }
    var picked by remember(note.id) { mutableStateOf<Note_revision?>(null) }
    LaunchedEffect(note.id) {
        revisions = withContext(Dispatchers.Default) { db.noteRevisionQueries.selectByNote(note.id).executeAsList() }
        picked = revisions.firstOrNull()
    }
    val shape = RoundedCornerShape(TeddTokens.RLg)
    Dialog(onDismissRequest = onClose, properties = DialogProperties(usePlatformDefaultWidth = false)) {
        Column(
            modifier = Modifier
                .padding(TeddSpace.S4)
                .widthIn(max = 760.dp)
                .fillMaxWidth()
                .heightIn(max = 620.dp)
                .teddOverlayShadow(shape)
                .background(TeddTokens.SurfaceOverlay, shape)
                .border(1.dp, TeddTokens.Line, shape)
                .padding(TeddSpace.S5),
            verticalArrangement = Arrangement.spacedBy(TeddSpace.S3),
        ) {
            Text("Historique des versions", style = TeddType.Title2, color = TeddTokens.Ink)
            Text(
                "Une version est gardée avant chaque séance d'écriture, vingt au plus, sur cet appareil.",
                style = TeddType.Caption,
                color = TeddTokens.InkMuted,
            )
            if (revisions.isEmpty()) {
                Text("Pas encore de version passée pour cette note.", style = TeddType.Body, color = TeddTokens.InkMuted)
            } else {
                Row(Modifier.weight(1f, fill = false).heightIn(min = 200.dp)) {
                    org.teddapp.project.ui.TeddSidePane(spec = VERSIONS_PANE, title = "les versions", icon = "clock") {
                    Column(
                        Modifier.fillMaxSize().verticalScroll(rememberScrollState()),
                        verticalArrangement = Arrangement.spacedBy(2.dp),
                    ) {
                        revisions.forEach { r ->
                            val active = picked?.id == r.id
                            Column(
                                Modifier
                                    .fillMaxWidth()
                                    .background(if (active) TeddTokens.Surface else TeddTokens.SurfaceOverlay, RoundedCornerShape(TeddTokens.RSm))
                                    .clickable { picked = r }
                                    .padding(horizontal = TeddSpace.S2, vertical = 8.dp),
                            ) {
                                Text(dayAndTime(r.created_at), style = TeddType.Label, color = if (active) TeddTokens.Ink else TeddTokens.InkMuted)
                                Text(
                                    MarkdownEdits.plain(r.body).ifBlank { "(vide)" },
                                    style = TeddType.Caption,
                                    color = TeddTokens.InkMuted,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis,
                                )
                            }
                        }
                    }
                    }
                    Box(
                        Modifier
                            .weight(1f)
                            .fillMaxHeight()
                            .padding(start = TeddSpace.S2)
                            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
                            .verticalScroll(rememberScrollState())
                            .padding(TeddSpace.S4),
                    ) {
                        picked?.let { r ->
                            Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
                                Text(r.title.orEmpty().ifBlank { "Sans titre" }, style = TeddType.Title2, color = TeddTokens.Ink)
                                Text(NoteExport.portableBody(r.body), style = TeddType.Body, color = TeddTokens.Ink)
                            }
                        }
                    }
                }
            }
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                val r = picked
                if (r != null) {
                    TeddButton(
                        text = "Restaurer cette version",
                        onClick = {
                            // La version actuelle d'abord, pour pouvoir revenir.
                            val now = nowMillis()
                            db.noteRevisionQueries.keep(note.id, draft.title, draft.body.text.toString(), note.updated_at, now)
                            draft.title = r.title.orEmpty()
                            draft.body.edit { replace(0, length, r.body) }
                            onClose()
                        },
                        kind = TeddButtonKind.Primary,
                        size = TeddButtonSize.Md,
                        icon = "undo",
                    )
                }
                TeddButton(text = "Fermer", onClick = onClose, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Md)
            }
        }
    }
}
